// import _ from 'lodash'
// import React from 'react'
import {
  OrderedMap,
  List,
  Map,
  fromJS,
  Iterable,
  Record
} from 'immutable'
import {
  TYPES,
  TYPE_DEFAULTS
} from './types'
// import FieldSetSchema from './FieldSetSchema'
import {
  mockEntry,
  mockTimeSeries
} from './utils/dataMocker'
import IT from 'react-immutable-proptypes'

const SchemaOptions = Record({
  itemDisplay: "",
  defaultColumnWidth: 150,
  defaultColumnHeight: 40,
  defaultNestedColumnHeight: 30,
  requiredFieldMsg: "Required Field"
})

const TABLE_COLUMN_DEFAULTS = Map({
  // key: null,
  // columns: undefined,
  // header: defJS.display || uiKey,
  // width: 200,
  // width: 100,
  // height: 100,

  resizable: true,
  sortable: false,
  fixed: false,
  stickyable: true // User can toggle stickiness
})

export default class Schema extends OrderedMap {
  constructor(initialSchema, options) {
    const newOptions = new SchemaOptions(options)

    // Initialize schema with default configuration
    const schemaWithDefaults = fromJS(initialSchema).map(Schema.applyDefaultsToField)

    super(schemaWithDefaults) // Populate the extended OrderedMap
    this.__options = newOptions
  }

  static mergeFieldWithDefault = (spec, defaultSpec) => {
    if (spec === true) {
      return defaultSpec
    }
    if (spec === false) {
      return spec
    }
    return Iterable.isIterable(spec) ? spec.mergeDeepWith(Schema.mergeFieldWithDefault, defaultSpec) : spec

  }

  static applyDefaultsToField = (field, fieldKey) => {
    const type = field.get('type')
    const nestedSchema = field.get('schema')

    if (type && TYPE_DEFAULTS.has(type)) {
      field = field.mergeDeepWith(Schema.mergeFieldWithDefault, TYPE_DEFAULTS.get(type))

      // For LISTs, deep merge default field settings for the `listItem` sub-field.
      if (type === TYPES.LIST) {
        const itemField = field.get('listItem')
        if (itemField) {
          const itemFieldType = itemField.get('type')
          field = field.set('listItem', itemField.mergeDeepWith(Schema.mergeFieldWithDefault, TYPE_DEFAULTS.get(itemFieldType)))
        }
        else {
          console.warn(`Field "${fieldKey}" marked as TYPES.LIST, but is missing a definition for "listItem".`)
        }
      }

      if (field.has('propType')) {
        if (field.get('required') && field.get('propType').hasOwnProperty('isRequired')) {
          field = field.set('propType', field.get('propType').isRequired) // Field is required, make the React PropType required as well
        }
      }
      else {
        console.warn(`Field "${fieldKey}" missing "propType" field. React PropTypes checking will not be enabled for this field. Certain TYPES (schema, list, etc.) do not have default PropTypes.`)
      }

      // Handle shared fieldSet groupings
      if (field.has('fieldSet')) {
        const fieldSet = field.get('fieldSet')
        if (fieldSet instanceof Map === false) {
          throw new Error('Schemer.Schema.editable.group must be an instance of Schemer.FieldSetSchema')
        }
        field = field.set('fieldSet', fieldSet) // Get stored schema definition of fieldSet
      }

      if (field.has('table')) {
        const fieldTable = field.get('table')
        let fieldColumn = TABLE_COLUMN_DEFAULTS.mergeDeep(typeof fieldTable === 'boolean' ? {} : fieldTable)
        // fieldColumn.set('key', fieldKey)
        if (nestedSchema) {
          fieldColumn = fieldColumn.set('columns', nestedSchema.getDataTableColumns())
        }
        field = field.set('table', fieldColumn)
      }
    }
    else {
      // throw new Error("Invalid Schema field type passed, or type is missing. Use TYPES.CUSTOM")
    }
    return field
  }

  /**
   * Return an OrderedMap of consistently formatted field options
   */
  static normalizeOptions = options => {
    // If options are a map of {<key>:<value>}, convert to a list
    if (Iterable.isKeyed(options)) {
      options = options.reduce((out, name, id) => out.push(List([id, name])), List())
    }

    return options.reduce((out, option) => {
      let _optionObj = {
        id: null,
        name: null,
        icon: null,
        count: null,
        headingText: null,
        readOnlyOption: false
      }
      if (typeof option === 'string') {
        // Handle simple string array/list options
        _optionObj.id = option
        _optionObj.name = option
      }
      else if (Iterable.isKeyed(option)) {
        if (!option.has('id')) {
          throw new Error(`Schema Options supplied as objects/maps must contain an 'id' field`)
        }
        _optionObj = Object.assign(_optionObj, option.toJS())
        _optionObj.name = option.get('name', option.get('id'))
      }
      else if (Iterable.isIndexed(option)) {
        // Handle 2D array optional format
        // e.g. [ ["id1", "Name 1"], ["id2", "Name 2"] ]
        _optionObj.id = option.get(0)
        _optionObj.name = option.get(1, option.get(0)) // Fallback to "id"
      } else {
        throw new Error(`Schema Options: Unrecognized option type`)
      }

      return out.set(_optionObj.id, Map(_optionObj))
    }, OrderedMap())
  }

  toString() {
    return this.__toString('Schemer.Schema (extends OrderedMap) {', '}')
  }

  // Override parent set() method with one that applies default schema values to
  // new schema fields
  set(fieldKey, newField) {
    super.set(fieldKey, Schema.applyDefaultsToField(newField, fieldKey))
  }

  setIn(keyPath, value) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Schema setIn(): Note that default schema values are not applied when deeply changing schema settings in this manner. `)
    }
    super.setIn(keyPath, value)
  }

  getItemDisplay() {
    return this.__options.get('itemDisplay')
  }

  /**
   * Prepare editable schema fields. Provide nesting for fieldSets.
   */
  _processEditableFields = schemaSubset => {
    return schemaSubset.reduce((out, field, fieldKey) => {
      const fieldSet = field.get(['fieldSet'])
      // Nest grouped items under a groupId key
      if (fieldSet) {
        const prev = out.get(fieldSet) || OrderedMap()
        out = out.set(fieldSet, prev.set(fieldKey, field))
      }
      else {
        out = out.set(fieldKey, field)
      }
      return out
    }, OrderedMap())
  }

  /**
   * Return all schema fields that are editable
   */
  getEditable = () => {
    return this._processEditableFields(
      this.filter(f => f.has('editable'))
        .filter(f => f.getIn(['editable', 'update'], true) !== false)
    )
  }

  /**
   * Return all schema fields that can be supplied at creation
   */
  getCreatable = () => {
    return this._processEditableFields(
      this.filter(f => f.has('editable'))
        .filter(f => f.getIn(['editable', 'create'], true) !== false)
    )
  }

  /**
   * Return all schema fields that can be graphed (have a truthy `graph` property)
   */
  getGraphable = () => {
    return this.filter(f => f.has('graph'))
        .filter(f => f.get('graph', false) !== false)
  }

  // _setMemoizedOption = (fieldKey, optionGetter) => {
  //   this._optionsCache =
  // }

  getFieldOptions = field => {
    let options = field.get('options')

    if (options && typeof options === 'function') {
      // FIXME support async retrieved options? async validation?
      options = options(field)
    }

    return Schema.normalizeOptions(options)
  }

  getFieldOptionsByName = fieldName => this.getFieldOptions(this.get(fieldName))

  getFieldOptionsJS = field => this.getFieldOptions(field).valueSeq().toJS()

  static hasValue = maybeValue => {
    let hasValue = !!maybeValue
    if (hasValue && maybeValue.isEmpty) {
      hasValue = !maybeValue.isEmpty()
    }
    return hasValue
  }

  validateField = (fieldValue, field, validateIsRequired = true) => {
    const requiredFieldMsg = this.__options.get('requiredFieldMsg')
    // const field = this.get(fieldKey)
    const type = field.get('type')
    const isRequired = field.get('required', false)
    const validateFn = field.getIn(['editable', 'validate'])
    // const
    if (type === TYPES.LIST) {
      // console.log('validateField-key', fieldKey)
      console.log('validateField-field', field)
      console.log('validateField-val', fieldValue)
    }

    // const itemValue = item.get(fieldKey)
    // const fieldValueJS = itemValue && itemValue.toJS ? itemValue.toJS() : itemValue
    const validateFnArgs = []
    // const hasValue = Array.isArray(itemValueJS) ? itemValueJS.length > 0 : itemValueJS != null
    const hasValue = Schema.hasValue(fieldValue)
    // ? false : (fieldValue.isEmpty ? fieldValue.isEmpty() : fieldValue)

    if (validateIsRequired && isRequired && !hasValue) {
      return requiredFieldMsg
    }
    else if (hasValue && validateFn) {
      if (type === TYPES.ENUM) {
        const fieldOptions = this.getFieldOptions(field)
        // itemValueJS = Array.isArray(itemValueJS) ? itemValueJS : [itemValueJS]
        validateFnArgs.push(fieldOptions)
      }
      // else if (type === TYPES.LIST) {
      //   const listItem = field.get('listItem')
      //   const listItemType = listItem.get('type')
      //   // console.log('validating LIST')
      //   if (listItemType) {

      //   }
      //   const listItemValidator =
      // }

      return validateFn(fieldValue, field, ...validateFnArgs)
    }

    return null
  }

  validate = (item, index) => {
    item = Map(item)
    let isValid = true

    const validationMessages = this.map((field, fieldKey) => {
      const fieldValue = item.get(fieldKey)
      const invalidMsg = this.validateField(fieldValue, field, true)
      if (invalidMsg) {
        isValid = false
      }
      return invalidMsg || null
    }).filter(invalidMsg => !!invalidMsg)

    return Map({
      isValid,
      validationMessages,
      item,
      index
    })
  }

  validateAll = items => fromJS(items).map(this.validate)

  /**
   * Returns all schema-defined prop-types and immutable prop-types as a plain JS object
   */
  getPropTypes = () => {
    return this.map(field => {
      let propType = field.get('propType')
      const nestedSchema = field.get('schema')
      if (nestedSchema) {
        propType = IT.listOf(IT.mapContains(nestedSchema.getPropTypes()))
      }
      return propType
    }).toJS()
  }

  getPropTypeShape = () => {
    return IT.mapContains(this.getPropTypes())
  }

  getPropTypeShapeList = () => {
    return IT.listOf(this.getPropTypeShape())
  }

  _defaultNestedColHeightFn = item => {
    const defaultNestedColumnHeight = this.__options.get('defaultNestedColumnHeight')

    // Column height may be a fixed number or a function, which is passed the datum
    // TODO should this recursively call nested schema height funcs?
    return Iterable.isIndexed(item) ? (item.size * defaultNestedColumnHeight) : defaultNestedColumnHeight
  }

  _mergeColumnPreference = (field, fieldPrefs, defaultColumnPrefs) => {
    const nestedSchema = field.get('schema')
    const fieldTableCol = field.get('table')
    let fieldColumnPref = defaultColumnPrefs
      .merge(typeof fieldTableCol === 'boolean' ? {} : fieldTableCol)
      .merge(fieldPrefs || {})

    if (nestedSchema) {
      const nestedTableColumns = nestedSchema.getDataTableColumns()
      let nestedWidth = 0
      let nestedHeaderHeight = 0

      nestedTableColumns.forEach(nestedCol => {
        nestedWidth += nestedCol.get('width', 0)
        nestedHeaderHeight += nestedCol.get('headerHeight', 0)
      })

      fieldColumnPref = fieldColumnPref.merge({
        columns: nestedTableColumns,
        headerHeight: nestedHeaderHeight,
        width: nestedWidth,
        height: this._defaultNestedColHeightFn
      })
    }

    return field.set('table', fieldColumnPref)
  }

  /**
   * Returns a set of column definitions formatted for passage to a pw-schemer Table component.
   * If `columnPrefs` (List) is passed, those values will be merged into Schema column definitions
   */
  getDataTableColumns = (columnPrefs) => {
    const defaultColumnWidth = this.__options.get('defaultColumnWidth')
    const defaultColumnHeight = this.__options.get('defaultColumnHeight')

    const defaultColumnPrefs = Map({
      // key: null,
      width: defaultColumnWidth,
      height: defaultColumnHeight,
      headerHeight: defaultColumnHeight,
      // columns: undefined
    })

    let cols = this.filter(field => field.has('table'))

    const keyedColumnPrefs = OrderedMap(columnPrefs || {}).mapKeys(pref => pref.get('key'))
    const columnPrefOrder = keyedColumnPrefs.keySeq().toList()

    if (columnPrefs && columnPrefs.size > 0) {
      cols = cols.filter(
        (field, fieldKey) => keyedColumnPrefs.has(fieldKey)
      )
      .sortBy(
        (field, fieldKey) => columnPrefOrder.indexOf(fieldKey)
      )
    }

    return cols
      .map(
        (field, fieldKey) => this._mergeColumnPreference(field, keyedColumnPrefs.get(fieldKey, {}), defaultColumnPrefs)
        // TODO merge in defaults from other getDefaultColumnPrefs func
      )
      .map(field => field.get('table'))
  }

  /**
   * For each field in Schema, coerce/sanitize
   * the passed `item`'s values to the proper data type
   */
  formatInbound = item => {
    item = fromJS(item)
    return item.map((itemVal, fieldKey) => {
      const field = this.get(fieldKey)
      const formatInbound = field.get('formatInbound')
      const nestedSchema = field.get('schema')
      // const itemVal = item.get(fieldKey)
      let val = formatInbound ? formatInbound(itemVal, item) : itemVal
      if (nestedSchema) {
        val = Iterable.isIndexed(val)
          ? val.map(innerItem => nestedSchema.formatInbound(innerItem))
          : nestedSchema.formatInbound(val)
      }
      return val
    })
  }

  formatOutbound = item => {
    item = fromJS(item)
    return item.map((itemVal, fieldKey) => {
      const field = this.get(fieldKey)
      if (!field) {
        return itemVal
      }

      const formatOutbound = field.get('formatOutbound')
      const nestedSchema = field.get('schema')
      let val = formatOutbound ? formatOutbound(itemVal, item) : itemVal
      if (nestedSchema) {
        val = Iterable.isIndexed(val)
          ? val.map(innerItem => nestedSchema.formatOutbound(innerItem))
          : nestedSchema.formatOutbound(val)
      }
      return val
    })
  }

  /**
   * Given an external supplied (e.g. API) item, use the Schema `path` definition
   * to populate/return a Schema compliant version.
   * Values also automatically passed through Schema.formatInbound
   * for defined type coercion as well
   */
  convertInbound = (item, pathOptions) => {
    if (!item) {
      // Falsy items returned verbatim
      return item
    }
    item = fromJS(item)

    const converted = this.map((field, fieldKey) => {
      const path = field.get('path')
      if (!path) {
        return null
      }
      const nestedSchema = field.get('schema')
      let inboundPath = typeof path === 'string' ? path : path.get('inbound') // Accept {path: {get: 'some.json.path', set: 'some.__other.path}}

      if (typeof inboundPath === 'function') {
        inboundPath = inboundPath(pathOptions)
      }
      let val = inboundPath ? item.getIn(inboundPath.split('.'), item.get(fieldKey, null)) : item.get(fieldKey, null)

      if (nestedSchema) {
        val = Iterable.isIndexed(val)
          ? val.map(innerVal => nestedSchema.convertInbound(innerVal, pathOptions))
          : nestedSchema.convertInbound(val, pathOptions)
      }

      return val
    })

    return this.formatInbound(converted)
  }

  convertOutbound = (item /*Map or Object*/, pathOptions) => {
    // Only return fields supplied on `item` (not entire schema)
    const outbound = fromJS(item).reduce((out, val, fieldKey) => {
      const field = this.get(fieldKey)
      if (!field) {
        return out
      }
      const path = field.get('path')
      if (!path) {
        return out
      }
      // const denormalize = field.get('denormalize')
      const nestedSchema = field.get('schema')
      const editable = field.get('editable')
      // const alwaysDenormalize = field.get('alwaysDenormalize')
      const required = field.get('required')

      let outboundPath = typeof path === 'string' ? path : path.get('outbound') // Accept {path: {get: 'some.json.path', set: 'some.__other.path}}
      if (typeof outboundPath === 'function') {
        outboundPath = outboundPath(pathOptions)
      }

      if ((required || !!editable) && outboundPath) {
        // Handle recursive Schema instances.
        if (nestedSchema && val) {
          val = Iterable.isIndexed(val) ? val.map(innerVal => nestedSchema.convertOutbound(innerVal, pathOptions)) : nestedSchema.convertOutbound(val, pathOptions)
        }
        return out.setIn(outboundPath.split('.'), val)
      }
      return out
    }, Map())

    return this.formatOutbound(outbound)
  }

  convertInboundAll = (items, pathOptions) => {
    return items.map(item => this.convertInbound(item, pathOptions))
  }

  convertOutboundAll = (items, pathOptions) => {
    return items.map(item => this.convertOutbound(item, pathOptions))
  }



  /**
   * Generate a dummy/mock data record using the current schema shape, matching the normalized object shape
   * TODO:
   * * Add option for generating normalized vs raw
   */
  generateMock = (x, idx) => {
    return mockEntry(this, idx)
  }

  generateMocks = itemCount => {
    return List(new Array(itemCount)).map(this.generateMock)
  }

  generateTimeSeriesMock = (startTime, endTime, intervalMs, mockSeedStr) => {
    // Assumes at least on DATETIME point in schema
    return mockTimeSeries(this, startTime, endTime, intervalMs, mockSeedStr)
  }
}
